'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { cn } from '@/lib/utils';
import type { PortableTextBlock } from 'sanity';

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ExamplesAccordion({
  content,
  triggerLabel = 'Examples',
}: {
  content: PortableTextBlock[];
  triggerLabel?: string;
}) {
  // Split blocks into standalone paragraphs vs bullet items.
  const paragraphs: PortableTextBlock[] = [];
  const bullets: PortableTextBlock[] = [];
  for (const b of content) {
    if ((b as any).listItem === 'bullet') bullets.push(b);
    else paragraphs.push(b);
  }

  const textOf = (b: PortableTextBlock) =>
    ((b as any).children ?? []).map((c: any) => c.text ?? '').join('');

  // If the first paragraph matches the trigger label, treat it as the header
  // (avoid rendering "Examples" twice).
  const header =
    paragraphs.length > 0 && textOf(paragraphs[0]).trim() === triggerLabel
      ? triggerLabel
      : triggerLabel;
  const bodyParagraphs =
    paragraphs.length > 0 && textOf(paragraphs[0]).trim() === triggerLabel
      ? paragraphs.slice(1)
      : paragraphs;

  return (
    <div className="w-full px-[2.75rem] border-t border-[#919191]/30 bg-black/[0.04]">
      <AccordionPrimitive.Root type="single" collapsible className="w-full">
        <AccordionPrimitive.Item value="examples">
          <AccordionPrimitive.Header>
            <AccordionPrimitive.Trigger className="group/trigger flex w-full items-center justify-between gap-[1.25rem] px-[1.25rem] py-[2.5rem] text-left outline-none">
              <p className="mb-0 font-geist font-normal text-[2.25rem] leading-[1.5] tracking-[-0.0675rem] text-black">
                {header}
              </p>
              <ChevronDown className="shrink-0 w-[3rem] h-[3rem] text-black transition-transform duration-200 group-data-[state=open]/trigger:rotate-180" />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>

          <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            <div className="flex flex-col gap-[1.25rem] px-[1.25rem] pb-[2.5rem]">
              {bodyParagraphs.map((b) => (
                <p
                  key={b._key}
                  className="mb-0 font-geist font-normal text-[2.25rem] leading-[1.5] tracking-[-0.0675rem] text-black"
                >
                  {textOf(b)}
                </p>
              ))}
              {bullets.length > 0 && (
                <ul className="list-disc block mb-0 ps-[3.375rem]">
                  {bullets.map((b, i) => (
                    <li
                      key={b._key}
                      className={cn(
                        'font-geist font-normal text-[2.25rem] leading-[1.5] tracking-[-0.0675rem] text-black/64',
                        i < bullets.length - 1 && 'mb-[1.25rem]',
                      )}
                    >
                      {textOf(b)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </AccordionPrimitive.Content>
        </AccordionPrimitive.Item>
      </AccordionPrimitive.Root>
    </div>
  );
}
